import {platform} from './transport';
import {ChatAttachmentSchema, type ChatAttachment} from '../../src/chat-attachments';

export type PendingChatInput = {requestId: string; text: string; attachmentIds: string[]; attachments: ChatAttachment[]};
export type SavedChatDrafts = {drafts: Record<string, string>; submissions: Record<string, PendingChatInput>};

const prefix = 'eightbots.desktop.chat-drafts.';
/** Only unsent text is stored locally; messages and submission receipts stay on the server. */
export function readChatDrafts(accountId: string | undefined): SavedChatDrafts {
  if (platform().kind !== 'desktop' || !accountId) return {drafts: {}, submissions: {}};
  const stored = localStorage.getItem(prefix + accountId);
  if (stored === null) return {drafts: {}, submissions: {}};
  const value: unknown = JSON.parse(stored);
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('drafts' in value) || !('submissions' in value) ||
    !value.drafts || typeof value.drafts !== 'object' || Array.isArray(value.drafts) ||
    Object.values(value.drafts).some(text => typeof text !== 'string') || !value.submissions || typeof value.submissions !== 'object' || Array.isArray(value.submissions)) {
    throw new Error('Saved desktop chat drafts have an invalid format.');
  }
  for (const input of Object.values(value.submissions)) {
    if (!input || typeof input !== 'object' || typeof input.requestId !== 'string' || !/^[a-f0-9-]{36}$/i.test(input.requestId) ||
      typeof input.text !== 'string' || !Array.isArray(input.attachmentIds) || input.attachmentIds.some((id: unknown) => typeof id !== 'string')) {
      throw new Error('A saved desktop submission has an invalid format.');
    }
    const attachments = ChatAttachmentSchema.array().parse(input.attachments);
    if (attachments.length !== input.attachmentIds.length || attachments.some((attachment, index) => attachment.id !== input.attachmentIds[index])) {
      throw new Error('The saved submission attachments do not match their server references.');
    }
  }
  return value as SavedChatDrafts;
}

export function saveChatDrafts(accountId: string | undefined, value: SavedChatDrafts): void {
  if (platform().kind !== 'desktop' || !accountId) return;
  const drafts = Object.fromEntries(Object.entries(value.drafts).filter(([, text]) => text.length > 0));
  if (Object.keys(drafts).length === 0 && Object.keys(value.submissions).length === 0) localStorage.removeItem(prefix + accountId);
  else localStorage.setItem(prefix + accountId, JSON.stringify({drafts, submissions: value.submissions}));
}

export function clearChatDrafts(accountId: string): void {
  if (platform().kind === 'desktop') localStorage.removeItem(prefix + accountId);
}
